import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { NavMode, Theme, TimeColMode } from '../worklog/types';
import { useLocalStorageString } from './useLocalStorage';

interface UIContextValue {
  theme: Theme;
  toggleTheme: () => void;
  timeColMode: TimeColMode;
  setTimeColMode: (m: TimeColMode) => void;
  navMode: NavMode;
  setNavMode: (m: NavMode) => void;
  notebookOpen: boolean;
  setNotebookOpen: (open: boolean) => void;
}

const UICtx = createContext<UIContextValue | null>(null);

function readTheme(): Theme {
  const ds = document.documentElement.dataset.theme;
  if (ds === 'light' || ds === 'dark') return ds;
  return 'dark';
}

export function UIProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(readTheme);
  const [timeColMode, setTimeColModeRaw] = useLocalStorageString('worklog::timeColMode', 'time');
  const [navMode, setNavModeRaw] = useLocalStorageString('worklog::navMode', 'category');
  const [notebookOpen, setNotebookOpen] = useState(false);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem('worklog::theme', theme);
    } catch {
      // unavailable
    }
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme(prev => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const setTimeColMode = useCallback((m: TimeColMode) => setTimeColModeRaw(m), [setTimeColModeRaw]);
  const setNavMode = useCallback((m: NavMode) => setNavModeRaw(m), [setNavModeRaw]);

  return (
    <UICtx.Provider
      value={{
        theme,
        toggleTheme,
        timeColMode: (timeColMode === 'both' ? 'both' : 'time') as TimeColMode,
        setTimeColMode,
        navMode: (navMode === 'priority' ? 'priority' : 'category') as NavMode,
        setNavMode,
        notebookOpen,
        setNotebookOpen,
      }}
    >
      {children}
    </UICtx.Provider>
  );
}

export function useUI(): UIContextValue {
  const v = useContext(UICtx);
  if (!v) throw new Error('useUI must be inside UIProvider');
  return v;
}
